import { Client } from '@twiliointernal/e2-commons-shared-api';
import {
  ErrorApiResponse,
  AccountSwitchResponse,
  ApiResponse,
  GetSubAccountsResponse,
  GetSubAccountsQueryParams,
  FeatureFlagsResponse,
  ErrorResponseIdConsole,
  EmptyObject,
  getOscConfig,
} from '@twiliointernal/e2-commons-shared-definitions';

import {
  ID_CONSOLE_DEFAULT_HEADERS,
  ID_CONSOLE_LOG_PREFIX,
} from '../definitions/constants';
import { setCurrentAccountCookies } from '../utils';

import { logger } from '@twiliointernal/e2-commons-shared-libs';

const { consoleEndpoint } = getOscConfig();

class IdConsole extends Client {
  constructor() {
    super(consoleEndpoint);
  }

  // http://id-api-docs.dev-us1.twilio.com/#id-console;post-/account-switcher/v1/active-account/-accountSid-
  switchAccount = async (accountSid, data): Promise<AccountSwitchResponse> => {
    const csrf = await this.getCsrf();

    const response: ApiResponse<AccountSwitchResponse> = await this.post({
      path: `/projects/${accountSid}`,
      body: data,
      headers: {
        ...ID_CONSOLE_DEFAULT_HEADERS,
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if ('projectSid' in response) {
      setCurrentAccountCookies(response.projectSid);
    }

    if (this.shouldLogWarning(response as ErrorResponseIdConsole)) {
      const errorResponse = response as ErrorResponseIdConsole;

      logger.warn(
        `${ID_CONSOLE_LOG_PREFIX}::[switchAccount]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as AccountSwitchResponse;
  };

  /**
   * Note: Response from API only has status code, so return empty object.
   *
   * http://id-api-docs.dev-us1.twilio.com/#id-console;get-/v2/console/account/switch/-accountSid-
   */
  switchSubAccount = async (accountSid: string): Promise<EmptyObject> => {
    const csrf = await this.getCsrf();

    const response = await this.get({
      path: `/account/api/v2/switch/${accountSid}`,
      headers: {
        ...ID_CONSOLE_DEFAULT_HEADERS,
        'x-twilio-csrf': csrf,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${ID_CONSOLE_LOG_PREFIX}::[switchSubAccount]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return {};
  };

  getFeatureFlags = async (): Promise<FeatureFlagsResponse> => {
    const response = await this.get({
      path: `/api/navigation/rpc?name=featureFlags&args=%7B%7D`,
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${ID_CONSOLE_LOG_PREFIX}::[getFeatureFlags]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as FeatureFlagsResponse;
  };

  // http://id-api-docs.dev-us1.twilio.com/#id-console;get-/account-subaccounts/v2/subaccounts
  getSubAccounts = async (
    queryParams: GetSubAccountsQueryParams = {},
  ): Promise<GetSubAccountsResponse> => {
    if (queryParams.search) {
      queryParams.search = encodeURIComponent(queryParams.search);
    }

    // URLSearchParams expects Record<string,string> type, can't resolve this without removing the queryParams type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params = new URLSearchParams(queryParams as any);

    const response = await this.get({
      path: `/account/api/v2/subaccounts?${params.toString()}`,
      headers: {
        ...ID_CONSOLE_DEFAULT_HEADERS,
      },
      requestOptions: {
        credentials: 'include',
      },
    });

    if (this.shouldLogWarning(response as ErrorApiResponse | Response)) {
      const errorResponse = response as ErrorApiResponse;

      logger.warn(
        `${ID_CONSOLE_LOG_PREFIX}::[getSubAccounts]::${JSON.stringify(
          errorResponse,
        )}`,
      );
    }

    return response as GetSubAccountsResponse;
  };
}

export default new IdConsole();
